import { makeObservable, observable, action } from 'mobx';
import { FactorEnrollmentStatus } from '@wix/ambassador-identity-factors-v1-identity-factor/types';
import { WebAuthnApi } from '../services/webAuthnApi';
import {
  EnrollmentStatus,
  PublicKeyCredentialWithJSON,
} from '../types/webAuthnEnrollmentTypes';
import { CreatePasskeyErrorCode } from '../types/createPasskeyMessages';
import { isWebAuthnSupported } from '../utils/webauthnCompat';
import { NotificationStore } from '../utils/notificationStore';
import { requestStepUpVerifier } from '../utils/stepUpVerifierHandshake';
import { exchangeCodeForToken } from '../services/stepUpTokenExchange';
import type { AppStore } from './AppStore';
import { HttpError } from '@wix/http-client';
import {
  createGlobalConfig,
  panoramaClientFactory,
  PanoramaErrorSeverity,
  PanoramaPlatform,
} from '@wix/panorama-client-web';
import type { PanoramaClientForComponent } from '@wix/panorama-client-web';

const INVALID_PASSWORD_ERROR_CODE = 9960;
// identity-factors EnrollFactor returns this coded application error when the
// server rejects the submitted credential as incorrect enrollment data.
const INCORRECT_ENROLLMENT_DATA_ERROR_CODE = 'INCORRECT_ENROLLMENT_DATA';
// The enroll call may still require step-up authentication (RFC 9470) even with
// the handed-off Bearer token. It surfaces either as a coded application error
// or as an HTTP 401 carrying the standard `insufficient_user_authentication`
// challenge in the WWW-Authenticate header.
const STEP_UP_REQUIRED_ERROR_CODE = 'STEP_UP_REQUIRED';
const INSUFFICIENT_USER_AUTH_CHALLENGE = 'insufficient_user_authentication';

const panoramaClient: PanoramaClientForComponent | undefined = (() => {
  try {
    return panoramaClientFactory({
      baseParams: {
        fullArtifactId: 'com.wixpress.webauthn-management',
        platform: PanoramaPlatform.Standalone,
      },
    })
      .withGlobalConfig(createGlobalConfig())
      .client({ baseParams: { componentId: 'passkey-enrollment' } })
      .createClientForComponent();
  } catch {
    return undefined;
  }
})();

const PANORAMA_SEVERITY: Record<string, PanoramaErrorSeverity> = {
  [CreatePasskeyErrorCode.CANCELLED]: PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.DUPLICATE_PASSKEY]: PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.NOT_SUPPORTED]: PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.CREDENTIAL_NOT_SUPPORTED]:
    PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.STEP_UP_REQUIRED]: PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.TIMEOUT]: PanoramaErrorSeverity.Error,
  [CreatePasskeyErrorCode.GENERAL]: PanoramaErrorSeverity.Error,
  WRONG_PASSWORD: PanoramaErrorSeverity.Warning,
};

function reportToPanorama(error: unknown, errorName: string): void {
  try {
    panoramaClient
      ?.errorMonitor()
      .reportError(error instanceof Error ? error : new Error(String(error)), {
        errorName,
        severity: PANORAMA_SEVERITY[errorName] ?? PanoramaErrorSeverity.Error,
      });
  } catch {
    // Intentional: Do not break the user flow on monitoring errors
  }
}

export class WebAuthnEnrollmentStore {
  private readonly appStore?: AppStore;
  private readonly webAuthnApi: WebAuthnApi;
  // Access token minted from the pre-authorized OAuth code in the step-up flow.
  // Kept in memory only (never persisted or logged) and attached as a Bearer
  // token on the enroll calls that would otherwise rely on password verification.
  private stepUpAccessToken?: string;

  enrollmentStatus: EnrollmentStatus = 'idle';
  passwordError: string | null = null;
  passwordStepNotification: NotificationStore = new NotificationStore();

  constructor(appStore?: AppStore) {
    this.appStore = appStore;
    this.webAuthnApi = new WebAuthnApi();

    makeObservable(this, {
      enrollmentStatus: observable,
      passwordError: observable,
      passwordStepNotification: observable,
      verifyPassword: action.bound,
      authorizeWithStepUpCode: action.bound,
      enrollWebAuthn: action.bound,
      sendForgotPasswordEmail: action.bound,
      clearPasswordError: action.bound,
      reset: action.bound,
    });
  }

  /**
   * Step-up alternative to verifyPassword: fetches the PKCE verifier from the
   * opener, exchanges the pre-authorized code for an access token, and stores it
   * for the enroll calls. Returns false (with the error surfaced) on any failure
   * so the caller shows the existing error UI — it never silently falls back to
   * password verification.
   */
  async authorizeWithStepUpCode(params: {
    stepUpCode: string;
    stepUpExchangeId: string;
    expectedOrigin: string;
  }): Promise<boolean> {
    this.enrollmentStatus = 'authenticating';
    try {
      const { codeVerifier, clientId, redirectUri } =
        await requestStepUpVerifier({
          exchangeId: params.stepUpExchangeId,
          expectedOrigin: params.expectedOrigin,
        });
      this.stepUpAccessToken = await exchangeCodeForToken({
        code: params.stepUpCode,
        clientId,
        redirectUri,
        codeVerifier,
      });
      return true;
    } catch (error) {
      this.enrollmentStatus = 'error';
      reportToPanorama(error, CreatePasskeyErrorCode.GENERAL);
      return false;
    }
  }

  async verifyPassword(password: string): Promise<boolean> {
    if (!password) {
      this.passwordError =
        'passkey.popups.creation.confirmModal.error.emptyPassword';
      return false;
    }
    if (!this.appStore) {
      throw new Error('verifyPassword requires AppStore');
    }
    this.enrollmentStatus = 'authenticating';
    this.passwordError = null;

    try {
      await this.appStore.accountSettingsApi.authenticate(password);
      return true;
    } catch (error) {
      this.enrollmentStatus = 'error';
      const errorCode = (error as HttpError).response?.data?.errorCode;
      if (errorCode === INVALID_PASSWORD_ERROR_CODE) {
        this.passwordStepNotification.close();
        this.passwordError =
          'passkey.popups.creation.confirmModal.error.wrongPassword';
        reportToPanorama(error, 'WRONG_PASSWORD');
      } else {
        this.passwordStepNotification.open({
          content: 'passkey.popups.creation.confirmModal.error.general',
          theme: 'error',
        });
        reportToPanorama(error, CreatePasskeyErrorCode.GENERAL);
      }
      return false;
    }
  }

  async enrollWebAuthn(): Promise<
    | { success: true; isBackedUp: boolean }
    | {
        success: false;
        errorCode: CreatePasskeyErrorCode;
        errorMessage: string;
      }
  > {
    this.enrollmentStatus = 'enrolling';

    if (!isWebAuthnSupported()) {
      this.enrollmentStatus = 'error';
      reportToPanorama(
        new Error('WebAuthn not supported'),
        CreatePasskeyErrorCode.NOT_SUPPORTED,
      );
      return {
        success: false,
        errorCode: CreatePasskeyErrorCode.NOT_SUPPORTED,
        errorMessage: 'WebAuthn is not supported in this browser',
      };
    }

    try {
      const challengeResponse = await this.webAuthnApi.enrollWebAuthn(
        undefined,
        this.stepUpAccessToken,
      );

      if (
        challengeResponse?.factorEnrollStatus !==
        FactorEnrollmentStatus.ENROLLMENT_REQUIRES_ADDITIONAL_STEP
      ) {
        throw new Error('Unexpected enrollment status');
      }

      const credentialCreationData =
        challengeResponse.enrollmentRequiresAdditionalStepData?.webauthnData;

      if (!credentialCreationData?.publicKeyCredentialCreationOptionsJson) {
        throw new Error('Missing WebAuthn creation options');
      }

      const { publicKey } = JSON.parse(
        credentialCreationData.publicKeyCredentialCreationOptionsJson,
      );
      const parsedCreationOptions = (
        PublicKeyCredential as unknown as PublicKeyCredentialWithJSON
      ).parseCreationOptionsFromJSON(publicKey);

      const newCredential = (await navigator.credentials.create({
        publicKey: parsedCreationOptions,
      })) as PublicKeyCredential | null;

      if (!newCredential) {
        throw new Error('Credential creation cancelled');
      }

      const completionResponse = await this.webAuthnApi.enrollWebAuthn(
        newCredential,
        this.stepUpAccessToken,
      );

      if (
        completionResponse?.factorEnrollStatus !==
        FactorEnrollmentStatus.ENROLLMENT_COMPLETE
      ) {
        throw new Error('Enrollment failed');
      }

      const isBackedUp = this.extractBackupState(newCredential);

      this.enrollmentStatus = 'success';
      return { success: true, isBackedUp };
    } catch (error) {
      this.enrollmentStatus = 'error';
      const { errorCode, errorMessage } = this.parseEnrollmentError(error);
      reportToPanorama(error, errorCode);
      return {
        success: false,
        errorCode,
        errorMessage,
      };
    }
  }

  private extractBackupState(credential: PublicKeyCredential): boolean {
    try {
      const response = credential.response as AuthenticatorAttestationResponse;
      const authenticatorData = response.getAuthenticatorData();
      const flags = new Uint8Array(authenticatorData)[32];
      // Bit 4 (0x10) is the BS (Backup State) flag
      return (flags & 0x10) !== 0;
    } catch {
      return false;
    }
  }

  private isStepUpRequiredError(error: unknown): boolean {
    // STEP_UP_REQUIRED only applies to the step-up flow — the bounce is "despite
    // the handed-off Bearer token". Without a step-up token this is the legacy
    // password flow, which must stay byte-identical (also keeps it behind the FT,
    // since the token is only ever set after the FT-gated authorization).
    if (!this.stepUpAccessToken) {
      return false;
    }
    const response = (error as HttpError).response;
    if (!response) {
      return false;
    }
    // Primary (and, per the account-settings contract, the signal that matters
    // in production): the STEP_UP_REQUIRED application error code. Matched
    // tolerantly — the code can surface at a few shapes across the gateway, and
    // we normalize case/whitespace — because the exact production payload
    // (pinned as account-settings' `productionStepUpError` fixture) is the
    // authority, not one hard-coded path.
    const data = response.data;
    const candidateCodes = [
      data?.details?.applicationError?.code,
      data?.applicationError?.code,
      data?.error?.code,
      data?.errorCode,
      data?.code,
    ];
    const matchesStepUpCode = candidateCodes.some(
      (code) =>
        typeof code === 'string' &&
        code.trim().toUpperCase() === STEP_UP_REQUIRED_ERROR_CODE,
    );
    if (matchesStepUpCode) {
      return true;
    }
    // Secondary fallback: RFC 9470 challenge — an HTTP 401 whose WWW-Authenticate
    // header carries error="insufficient_user_authentication". (Some gateways
    // strip this header, which is why the coded application error above is the
    // signal that matters.)
    const wwwAuthenticate = String(
      response.headers?.['www-authenticate'] ?? '',
    ).toLowerCase();
    return (
      response.status === 401 &&
      wwwAuthenticate.includes(INSUFFICIENT_USER_AUTH_CHALLENGE)
    );
  }

  private parseEnrollmentError(error: unknown): {
    errorCode: CreatePasskeyErrorCode;
    errorMessage: string;
  } {
    // The enroll call still requires step-up authentication even with the
    // handed-off Bearer token; report it so account-settings shows the dedicated
    // snackbar (no retry, no password fallback).
    if (this.isStepUpRequiredError(error)) {
      return {
        errorCode: CreatePasskeyErrorCode.STEP_UP_REQUIRED,
        errorMessage: 'Step-up authentication required for enrollment',
      };
    }

    // The server rejects an unusable credential with this coded application
    // error; map it to a specific client code (same inline HttpError code
    // check as verifyPassword above).
    const serverErrorCode = (error as HttpError).response?.data?.details
      ?.applicationError?.code;
    if (serverErrorCode === INCORRECT_ENROLLMENT_DATA_ERROR_CODE) {
      return {
        errorCode: CreatePasskeyErrorCode.CREDENTIAL_NOT_SUPPORTED,
        errorMessage: `Server rejected credential: ${serverErrorCode}`,
      };
    }

    if (error instanceof DOMException) {
      const errorMessage = `${error.name}: ${error.message}`;

      if (error.name === 'InvalidStateError') {
        return {
          errorCode: CreatePasskeyErrorCode.DUPLICATE_PASSKEY,
          errorMessage,
        };
      }

      if (error.name === 'NotAllowedError') {
        return {
          errorCode: CreatePasskeyErrorCode.CANCELLED,
          errorMessage,
        };
      }

      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        return {
          errorCode: CreatePasskeyErrorCode.TIMEOUT,
          errorMessage,
        };
      }

      return {
        errorCode: CreatePasskeyErrorCode.GENERAL,
        errorMessage,
      };
    }

    if (error instanceof Error) {
      return {
        errorCode: CreatePasskeyErrorCode.GENERAL,
        errorMessage: `Error: ${error.message}`,
      };
    }

    return {
      errorCode: CreatePasskeyErrorCode.GENERAL,
      errorMessage: `Unknown: ${String(error)}`,
    };
  }

  async sendForgotPasswordEmail(): Promise<void> {
    if (!this.appStore) {
      throw new Error('sendForgotPasswordEmail requires AppStore');
    }
    const email = this.appStore.userDetails?.email;
    if (!email) {
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.error.general',
        theme: 'error',
      });
      return;
    }

    try {
      await this.appStore.accountSettingsApi.sendResetPasswordEmail(email);
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.success.notification',
        theme: 'success',
      });
    } catch {
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.error.general',
        theme: 'error',
      });
    }
  }

  clearPasswordError(): void {
    this.passwordError = null;
  }

  reset(): void {
    this.enrollmentStatus = 'idle';
    this.passwordError = null;
    this.passwordStepNotification.close();
  }
}
